import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FaArrowLeft } from 'react-icons/fa';
import TalentInfoHeader from '../Utils/TalentInfoHeader';
import ProfileMetricCard from './Shards/ProfileMetricCard';
import AdditionalMetricCard from './Shards/AdditionalMetricCard';
import StrengthsConcernsCard from './Shards/StrengthsConcernsCard';
import HardSkillsSection from './Shards/HardSkillsSection';
import SoftSkillsSection from './Shards/SoftSkillsSection';
import AreasOfExpertise from './Shards/AreasOfExpertise';
import JobResponsibilitiesFit from './Shards/JobResponsibilitiesFit';

const PrintableJobProfileFit = ({ showSources: propShowSources = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    fitJobProfile, 
    candidateProfile,
    roleCategory,
    showSources: locationShowSources
  } = location.state || {};
  
  // Use the showSources from location state if provided, otherwise use the prop value
  const showSources = locationShowSources !== undefined ? locationShowSources : propShowSources;

  // Parse data if it's a string
  const parsedFitJobProfile = typeof fitJobProfile === 'string' ? JSON.parse(fitJobProfile) : fitJobProfile;

  // Extract data from candidateProfile if available
  const name = candidateProfile ? (candidateProfile.firstname + ' ' + candidateProfile.lastname || 'N/A') : 'N/A';
  const roleCategoryText = roleCategory?.mainCategory || ""; 
  const role = roleCategory?.subCategory || candidateProfile?.role?.content || "N/A";
  const totalYearsOfExperience = candidateProfile?.totalYearsOfExperience?.years || 'N/A';
  
  // Extract data from the parsed fitJobProfile if available
  const seniority = parsedFitJobProfile?.seniority || {};
  const conclusion = parsedFitJobProfile?.conclusion || {};
  const generalAbility = parsedFitJobProfile?.generalAbility || {};
  const jobFit = parsedFitJobProfile?.jobFit || {};
  const aiProficiency = parsedFitJobProfile?.["AI Proficiency"] || {};
  const multidisciplinaryExpertise = parsedFitJobProfile?.["Multidisciplinary Expertise"] || {};
  const securityAwareness = parsedFitJobProfile?.["Security Awareness"] || {};
  const strengths = parsedFitJobProfile?.strengths || {};
  const concerns = parsedFitJobProfile?.concerns || {};
  const hardSkills = parsedFitJobProfile?.hardSkills || {};
  const softSkills = parsedFitJobProfile?.softSkills || {};
  const expertise = parsedFitJobProfile?.expertise || {};
  const responsibilitiesFit = parsedFitJobProfile?.responsibilitiesFit || {};



  return (
    <div className="p-8 max-w-5xl mx-auto bg-white">
      {/* Back button - only visible on screen, not when printing */}
      <button 
        onClick={() => navigate(-1)} 
        className="mb-6 flex items-center text-blue-600 hover:text-blue-800 print:hidden"
      >
        <FaArrowLeft className="mr-2" /> Back to Job Profile Fit
      </button>

      {/* Page title - only visible on screen, not when printing */}
      <h1 className="text-3xl font-bold mb-8 text-center print:hidden">Printable Job Profile Fit</h1>
      
      {/* Print instructions - only visible on screen, not when printing */}
      <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg print:hidden">
        <p className="text-blue-800">
          This page is optimized for printing. Use Cmd+P (Mac) or Ctrl+P (Windows) to print or save as PDF.
          The header and this message will not appear in the printed version.
        </p>
      </div>

      {/* Report title - visible in print */}
      <h1 className="text-3xl font-bold mb-6 text-center hidden print:block">Job Profile Fit Assessment</h1>
      
      {/* Header with candidate info */}
      <TalentInfoHeader
        name={name}
        roleCategory={roleCategoryText}
        role={role}
        yearsOfExperience={totalYearsOfExperience}
        seniority={seniority}
        showRationale={true}
      />
      
      {/* Conclusion Section */}
      <div className="print-break-inside-avoid">
        <div className="border border-blue-200 rounded-lg p-6 bg-blue-50">
          <div className="flex justify-between items-start mb-3">
            <h4 className="text-lg font-semibold text-gray-800">Conclusion</h4>
            {showSources && conclusion?.source && (
              <span className="text-xs text-gray-500">Source: {conclusion.source}</span>
            )}
          </div>
          <div>
            <p className="text-gray-800 text-lg">{conclusion?.value || "No conclusion available."}</p>
          </div>
        </div>
      </div>
      
      {/* General Ability and Job Fit Section */}
      <div className="mt-6 print-break-inside-avoid">
        {/* General Ability Chart */}
        <ProfileMetricCard
          title="General Ability"
          value={generalAbility?.value || 0}
          rationale={generalAbility?.rationale}
          source={generalAbility?.source}
          showSource={showSources}
        />
        
        {/* Job Fit Chart */}
        <div className="mt-4">
          <ProfileMetricCard
            title="Fit for Job"
            value={jobFit?.value || 0}
            rationale={jobFit?.rationale}
            source={jobFit?.source}
            showSource={showSources}
          />
        </div>
      </div>
      
      {/* Additional Metrics Section */}
      <div className="mt-6 print-break-inside-avoid">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">Additional Key Metrics</h4>
          <div className="space-y-4">
            {/* AI Proficiency */}
            <AdditionalMetricCard 
              title="AI Proficiency"
              value={aiProficiency?.value || 0}
              rationale={aiProficiency?.rationale}
              source={aiProficiency?.source}
              showSource={showSources}
            />
            
            {/* Multidisciplinary Expertise */}
            <AdditionalMetricCard 
              title="Multidisciplinary Expertise"
              value={multidisciplinaryExpertise?.value || 0}
              rationale={multidisciplinaryExpertise?.rationale}
              source={multidisciplinaryExpertise?.source}
              showSource={showSources}
            />
            
            {/* Security Awareness */}
            <AdditionalMetricCard 
              title="Security Awareness"
              value={securityAwareness?.value || 0}
              rationale={securityAwareness?.rationale}
              source={securityAwareness?.source}
              showSource={showSources}
            />
          </div>
        </div>
      </div>
      
      {/* Strengths and Concerns Section */}
      <div className="mt-6 print-break-inside-avoid">
        <StrengthsConcernsCard 
          strengths={strengths}
          concerns={concerns}
          showSources={showSources}
        />
      </div>
      
      {/* Hard Skills Section */}
      <div className="mt-6 print-break-inside-avoid">
        <HardSkillsSection 
          hardSkills={hardSkills}
          radarChartMode="all" 
          showSources={showSources}
        />
      </div>
      
      {/* Soft Skills Section */}
      <div className="mt-6 print-break-inside-avoid">
        <SoftSkillsSection 
          softSkills={softSkills}
          showSources={showSources}
        />
      </div>
      
      {/* Areas of Expertise Section */}
      <div className="mt-6 print-break-inside-avoid">
        <AreasOfExpertise 
          expertise={expertise}
          showSources={showSources}
        />
      </div>
      
      {/* Job Responsibilities Fit Section */}
      <div className="mt-6 print-break-inside-avoid">
        <JobResponsibilitiesFit 
          responsibilitiesFit={responsibilitiesFit}
          showSources={showSources}
        />
      </div>
      
      {/* Footer with page number - only visible when printing */}
      <div className="mt-8 text-center text-gray-500 text-sm hidden print:block">
        <p>Generated on {new Date().toLocaleDateString()}</p>
      </div>
      
      {/* Add print-specific CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
      @page {
        size: A4;
        margin: 0.8cm;
      }
            body {
              font-size: 12pt;
              color: #000;
              background-color: #fff;
            }
            .print-break-inside-avoid {
              break-inside: avoid;
            }
            .print-break-before {
              break-before: page;
            }
            /* Force flex-row layout in print view */
            .flex-col.md\\:flex-row {
              flex-direction: row !important;
            }
            /* Ensure left column maintains width in print view */
            .md\\:w-1\\/3 {
              width: 33.333333% !important;
              min-width: 200px !important;
              flex-shrink: 0 !important;
            }
            /* Ensure right column takes remaining width in print view */
            .md\\:w-2\\/3 {
              width: 66.666667% !important;
              padding-left: 20px !important;
            }
          }
        `
      }} />
    </div>
  );
};

PrintableJobProfileFit.propTypes = {
  showSources: PropTypes.bool
};

export default PrintableJobProfileFit;
